import React from "react";
import {
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Package,
  Tag,
  TrendingDown,
  TrendingUp,
  Minus,
  AlertCircle,
  CheckCircle,
  Clock,
} from "lucide-react";

import AppButton from "../AppButton";
import PriceHistoryChart from "../PriceHistoryChart";
import { VariantData } from "../../../interface";

interface VariantsListProps {
  groupedByUrl: Record<string, VariantData[]>;
  expandedUrls: Set<string>;
  toggleUrlExpansion: (url: string) => void;
  handleOpenModal: (e: React.MouseEvent, url: string) => void;
  canEdit: boolean;
  productId: number;
}

const VariantsList: React.FC<VariantsListProps> = ({
  groupedByUrl,
  expandedUrls,
  toggleUrlExpansion,
  handleOpenModal,
  canEdit,
  productId,
}) => {
  const getPriceChangeIcon = (priceChange?: string) => {
    switch (priceChange) {
      case "down":
        return <TrendingDown size={20} className="text-danger" />;
      case "up":
        return <TrendingUp size={20} className="text-success" />;
      default:
        return <Minus size={20} className="text-muted" />;
    }
  };

  const getPriceChangeColor = (priceChange?: string) => {
    switch (priceChange) {
      case "down":
        return "text-danger";
      case "up":
        return "text-success";
      default:
        return "text-dark";
    }
  };

  const getStockStatusBadge = (stockStatus?: string) => {
    const status = stockStatus?.toLowerCase() || "";

    if (status.includes("out of stock")) {
      return (
        <span className="badge bg-danger d-inline-flex align-items-center">
          <AlertCircle size={12} className="me-1" />
          Out of Stock
        </span>
      );
    } else if (status.includes("limited")) {
      return (
        <span className="badge bg-warning d-inline-flex align-items-center">
          <Clock size={12} className="me-1" />
          Limited Stock
        </span>
      );
    } else if (status.includes("in stock")) {
      return (
        <span className="badge bg-success d-inline-flex align-items-center">
          <CheckCircle size={12} className="me-1" />
          In Stock
        </span>
      );
    }

    return stockStatus ? (
      <span className="badge bg-secondary d-inline-flex align-items-center">
        <Package size={12} className="me-1" />
        {stockStatus}
      </span>
    ) : null;
  };

  return (
    <>
      <style>{`
        .variant-card {
          transition: all 0.3s ease;
          border: 1px solid #dee2e6;
          border-radius: 1rem;
        }
        .variant-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.15);
          border-color: #0d6efd;
        }
        .url-header {
          background: linear-gradient(135deg, #f0f9ff 0%, #e0e7ff 100%);
          border-radius: 1rem 1rem 0 0;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .url-header:hover {
          background: linear-gradient(135deg, #dbeafe 0%, #c7d2fe 100%);
        }
        .collapse-icon {
          transition: transform 0.2s ease;
        }
        .collapse-icon.rotated {
          transform: rotate(180deg);
        }
        .price-text {
          font-size: 2rem;
          font-weight: bold;
        }
        .sku-badge {
          font-family: 'Courier New', monospace;
          font-size: 0.75rem;
        }
        .chart-container {
          height: 300px;
          min-height: 250px;
          border: 1px solid #dee2e6;
          border-radius: 1rem;
          padding: 1rem;
          background: #fff;
        }
      `}</style>
      <div className="container px-3 px-md-4">
        <div className="d-flex flex-column gap-4">
          {Object.entries(groupedByUrl).map(([url, variants]) => {
            // 🔥 Filter out variants where currentPrice is null or undefined
            const filteredVariants = variants.filter(
              (v) => v.currentPrice !== null && v.currentPrice !== undefined
            );

            // If all variants are filtered out, skip rendering this URL block
            if (filteredVariants.length === 0) return null;

            return (
              <div
                key={url}
                className="card border-0 shadow-sm"
                style={{ borderRadius: "1rem", overflow: "hidden" }}
              >
                {/* URL Header */}
                <div
                  className="url-header p-3 border-bottom"
                  onClick={() => toggleUrlExpansion(url)}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    {/* Left Section: Icon + URL */}
                    <div className="d-flex align-items-center gap-3 flex-grow-1 overflow-hidden">
                      <ExternalLink
                        size={20}
                        className="text-primary flex-shrink-0"
                      />
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mb-0 fw-semibold text-decoration-none text-dark text-truncate"
                        style={{
                          maxWidth: "100%", // Prevents overflowing container
                          display: "inline-block",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {url}
                      </a>
                    </div>

                    {/* Right Section: Button + Chevron */}
                    <div className="d-flex align-items-center gap-2 flex-shrink-0 ms-3">
                      {canEdit && (
                        <AppButton
                          label="Update URL"
                          size="sm"
                          onClick={(e) => handleOpenModal(e, url)}
                        />
                      )}
                      <div
                        className={`collapse-icon ${
                          expandedUrls.has(url) ? "rotated" : ""
                        }`}
                      >
                        {expandedUrls.has(url) ? (
                          <ChevronUp size={20} className="text-muted" />
                        ) : (
                          <ChevronDown size={20} className="text-muted" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className={`collapse ${expandedUrls.has(url) ? "show" : ""}`}
                >
                  <div className="card-body p-4">
                    {filteredVariants.length === 1 ? (
                      // ✅ Single Variant Layout
                      <div className="row g-4">
                        <div className="col-12 col-lg-6">
                          <div className="card variant-card h-100">
                            <div className="card-body p-4">
                              <h6 className="card-title fw-semibold mb-3">
                                {filteredVariants[0].variantName || null}
                              </h6>
                              {/* SKU */}
                              {filteredVariants[0].sku && (
                                <div className="mb-3">
                                  <div className="d-flex align-items-center gap-2">
                                    <Tag size={16} className="text-muted" />
                                    <small className="text-muted fw-medium">
                                      SKU:
                                    </small>
                                    <span className="badge bg-light text-dark sku-badge">
                                      {filteredVariants[0].sku}
                                    </span>
                                  </div>
                                </div>
                              )}
                              {/* Stock Status */}
                              <div className="mb-4">
                                {getStockStatusBadge(
                                  filteredVariants[0].stockStatus ?? undefined
                                )}
                              </div>

                              {/* Price Section */}
                              <div className="border-top pt-3">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div className="flex-grow-1">
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                      <span
                                        className={`price-text ${getPriceChangeColor(
                                          filteredVariants[0].priceChange ??
                                            undefined
                                        )}`}
                                      >
                                        {filteredVariants[0].currentPrice}
                                      </span>
                                      {getPriceChangeIcon(
                                        filteredVariants[0].priceChange ??
                                          undefined
                                      )}
                                    </div>

                                    {filteredVariants[0].originalPrice &&
                                      filteredVariants[0].originalPrice !==
                                        filteredVariants[0].currentPrice && (
                                        <div className="d-flex align-items-center gap-2">
                                          <small className="text-muted">
                                            Original Price:
                                          </small>
                                          <small className="text-muted text-decoration-line-through">
                                            {filteredVariants[0].originalPrice}
                                          </small>
                                        </div>
                                      )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Price History Chart */}
                        <div className="col-12 col-lg-6">
                          <div className="chart-container h-100">
                            <PriceHistoryChart
                              productId={productId}
                              variants={[
                                {
                                  id: filteredVariants[0].variantId,
                                  name:
                                    filteredVariants[0].variantName ??
                                    "Variant",
                                },
                              ]}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      // ✅ Multiple Variants Layout
                      <>
                        <div className="row g-4 mb-4">
                          {filteredVariants.map((variant) => (
                            <div
                              key={variant.variantId}
                              className="col-12 col-lg-6 col-xl-4"
                            >
                              <div className="card variant-card h-100">
                                <div className="card-body p-4">
                                  <h6 className="card-title fw-semibold mb-3">
                                    {variant.variantName || null}
                                  </h6>
                                  {/* SKU */}
                                  {variant.sku && (
                                    <div className="mb-3">
                                      <div className="d-flex align-items-center gap-2">
                                        <Tag size={16} className="text-muted" />
                                        <small className="text-muted fw-medium">
                                          SKU:
                                        </small>
                                        <span className="badge bg-light text-dark sku-badge">
                                          {variant.sku}
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                  {/* Stock Status */}
                                  <div className="mb-4">
                                    {getStockStatusBadge(
                                      variant.stockStatus ?? undefined
                                    )}
                                  </div>

                                  {/* Price Section */}
                                  <div className="border-top pt-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                      <div className="flex-grow-1">
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                          <span
                                            className={`price-text ${getPriceChangeColor(
                                              variant.priceChange ?? undefined
                                            )}`}
                                          >
                                            {variant.currentPrice}
                                          </span>
                                          {getPriceChangeIcon(
                                            variant.priceChange ?? undefined
                                          )}
                                        </div>

                                        {variant.originalPrice &&
                                          variant.originalPrice !==
                                            variant.currentPrice && (
                                            <div className="d-flex align-items-center gap-2">
                                              <small className="text-muted">
                                                Original Price:
                                              </small>
                                              <small className="text-muted text-decoration-line-through">
                                                {variant.originalPrice}
                                              </small>
                                            </div>
                                          )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="chart-container mt-3">
                          <PriceHistoryChart
                            productId={productId}
                            variants={filteredVariants.map(
                              (variant, index) => ({
                                id: variant.variantId,
                                name:
                                  variant.variantName ?? `Variant ${index + 1}`,
                              })
                            )}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default VariantsList;
